<template>
  <div class="AtlasDownload">
    <div class="AtlasDownload-icons">
      <span
        class="AtlasDownload-icon"
        title="Download"
        :class="{ disabled: disabledDownloadImage }"
      >
        <atlas-icon
          @click.prevent="downloadFile(downloadImage)"
          name="download-image"
          :size="22"
          caption="Download Image"
        />
      </span>

      <span
        class="AtlasDownload-icon"
        title="Download"
        :class="{ disabled: disabledDownloadPowerpoint }"
      >
        <atlas-icon
          @click.prevent="downloadPowerpoint()"
          name="download-powerpoint"
          :size="22"
          caption="Download Powerpoint"
        />
      </span>

      <span
        class="AtlasDownload-icon"
        title="Download"
        :class="{ disabled: disabledDownloadXlsx }"
      >
        <atlas-icon
          @click.prevent="downloadXlsx()"
          name="download-excel"
          :size="22"
          caption="Download Excel"
        />
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';

export default {
  name: 'atlas-download',

  props: {
    breakdownId: {
      type: String,
      required: true,
    },
    disabledDownloadImage: {
      type: Boolean,
      default: () => false,
    },
    disabledDownloadPowerpoint: {
      type: Boolean,
      default: () => false,
    },
    disabledDownloadXlsx: {
      type: Boolean,
      default: () => false,
    },
  },

  computed: {
    ...mapState('display', ['theme']),

    ...mapState('indicator', ['indicator', 'indicatorId']),

    ...mapState('breakdown', ['primaryGeo', 'secondaryGeo', 'geoType']),

    ...mapGetters('indicator', ['indicatorConfig']),

    ...mapGetters('breakdown', [
      'breakdownById',
      'dataByBreakdownId',
      'tokenContextByBreakdownId',
      'contextByBreakdownId',
      'captionByBreakdownId',
    ]),

    ...mapGetters('breakdown/query', {
      queryParams: 'params',
    }),

    breakdown() {
      return this.breakdownById(this.breakdownId);
    },
    data() {
      return this.dataByBreakdownId(this.breakdownId);
    },
    tokenContext() {
      return this.tokenContextByBreakdownId(this.breakdownId);
    },
    context() {
      return this.contextByBreakdownId(this.breakdownId);
    },

    caption() {
      return this.captionByBreakdownId(this.breakdownId);
    },

    getActiveCombination() {
      const combinations = this.breakdown.combinations || [];
      return combinations.find((comb) =>
        Object.keys(comb.contexts).every(
          (key) => comb.contexts[key] === this.context[key],
        ),
      );
    },

    getActiveCombinationKeys() {
      return Object.keys(this.getActiveCombination?.contexts || []);
    },

    parsedDownloadableFields() {
      const fields = Object.values(this.getActiveCombination?.fields || []);
      if (this.breakdown.type === 'map' || this.breakdown.subtype === 'rank') {
        fields.unshift('GEO_NAME', 'geo_code_long');
      }
      return fields;
    },

    parsedDownloadableFilters() {
      return Object.entries(this.context).filter(
        (item) => !this.getActiveCombinationKeys.includes(item[0]),
      );
    },

    geo() {
      // For maps and rank charts, filter on the geo type.
      if (this.breakdown.type === 'map' || this.breakdown.subtype === 'rank') {
        return this.geoType ? { geoType: this.geoType } : {};
      }
      // For other charts, filter on the selected geos.
      return {
        ...(this.primaryGeo && this.primaryGeo.id
          ? { primaryGeo: this.primaryGeo.id }
          : {}),
        ...(this.secondaryGeo && this.secondaryGeo.id
          ? { secondaryGeo: this.secondaryGeo.id }
          : {}),
      };
    },
    downloadXlsxFilters() {
      const filters = {};

      if (this.geoType) {
        filters.geo_type = [this.geoType];
      }

      filters.geo_code_long = [
        this.primaryGeo && this.primaryGeo.id ? this.primaryGeo.id : '',
        this.secondaryGeo && this.secondaryGeo.id ? this.secondaryGeo.id : '',
      ];

      return filters;
    },
  },

  methods: {
    ...mapMutations('ui', ['closeDropdown']),

    ...mapMutations('breakdown', ['setIsDownloadLoading']),

    async downloadImage() {
      const url = new URL(window.location.href);
      url.searchParams.set('screenshot', 'true');
      return this.$api.getImagePath(url.href);
    },

    async downloadPowerpoint() {
      this.setIsDownloadLoading({ isDownloadLoading: true });
      this.$api
        .fetchPowerpoint({
          indicator: this.queryParams.indicator_id,
          display: this.queryParams.display_id,
          breakdown: this.breakdownId,
          tokenContext: this.tokenContext,
          screenshotUrl: window.location.href,
          caption: this.caption,
          theme: this.theme,
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'],
          });

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          const cd = response.headers['content-disposition'];
          let filename = 'presentation.pptx';
          if (cd) {
            const match = cd.match(/filename="?([^";]+)"?/);
            if (match) filename = match[1];
          }
          link.setAttribute('download', filename);

          link.click();

          window.URL.revokeObjectURL(url);
          this.setIsDownloadLoading({ isDownloadLoading: false });
        });
    },

    async downloadXlsx() {
      this.setIsDownloadLoading({ isDownloadLoading: true });
      this.$api
        .fetchExcel({
          indicator: this.queryParams.indicator_id,
          display: this.queryParams.display_id,
          breakdown: this.breakdownId,
          filters: this.downloadXlsxFilters,
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'],
          });

          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          const cd = response.headers['content-disposition'];
          let filename = 'data.xlsx';
          if (cd) {
            const match = cd.match(/filename="?([^";]+)"?/);
            if (match) filename = match[1];
          }
          link.setAttribute('download', filename);

          link.click();

          window.URL.revokeObjectURL(url);
          this.setIsDownloadLoading({ isDownloadLoading: false });
        });
    },

    async downloadFile(action) {
      this.setIsDownloadLoading({ isDownloadLoading: true });

      try {
        const path = await action();
        if (path) window.open(path);
      } finally {
        this.setIsDownloadLoading({ isDownloadLoading: false });
      }

      this.closeDropdown();
    },
  },
};
</script>

<style lang="scss">
.AtlasDownload {
  &-icons {
    @include clearfix;
  }

  &-link {
    @include size(45px);
    display: block;
    cursor: pointer;
    float: left;

    &:hover {
      background-color: #cfd3d7;
    }
  }

  // Extra specificity to override .Indicator a
  .AtlasDownload-link {
    color: inherit;
  }

  &-icon {
    position: relative;
    display: inline-block;
    padding: 13px 10px 10px;
    cursor: pointer;
    line-height: 22px;
    vertical-align: top;

    &:hover {
      background-color: #cfd3d7;
    }
  }

  .disabled {
    pointer-events: none;
    opacity: 0.3;
  }
}
</style>
